import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  useLensCollaborationMutation,
  useLensRevision,
  useLensRevisions,
} from '~/hooks/apis/reporting/useLensCollaboration';

import type { Lens, LensResponse } from '~/types/leLens';

import { editableLensDefinition, lensChangeLabel, lensDefinitionChanges } from './lensDefinitionChanges';

export default function LensCollaborationDialog({
  lens,
  access,
  open,
  onClose,
}: {
  lens: Lens;
  access: LensResponse['access'];
  open: boolean;
  onClose: () => void;
}) {
  const [revision, setRevision] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const [revisionOffset, setRevisionOffset] = useState(0);
  const revisions = useLensRevisions(lens.id, open && access.canEdit, revisionOffset);
  const snapshot = useLensRevision(lens.id, revision);
  const mutation = useLensCollaborationMutation(lens.id);
  const current = { title: lens.title, description: lens.description, spec: lens.spec };
  const candidate = snapshot.data
    ? {
        title: snapshot.data.lens.title,
        description: snapshot.data.lens.description,
        spec: snapshot.data.lens.spec,
      }
    : undefined;
  const changes = candidate
    ? lensDefinitionChanges(editableLensDefinition(current), editableLensDefinition(candidate))
    : [];
  const failure = mutation.error ?? revisions.error ?? snapshot.error;
  const run = async (suffix: string, body: unknown) => {
    try {
      await mutation.mutateAsync({ suffix, body });
      setRevision(null);
      setNotice('Change recorded.');
    } catch {
      setNotice('');
    }
  };
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => !mutation.isPending && onClose()}>
      <DialogTitle>{lens.title} · Version history</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {notice && <Alert severity="success">{notice}</Alert>}
          {failure && (
            <Alert severity="error">
              {failure instanceof Error ? failure.message : 'Could not complete this request.'}
            </Alert>
          )}
          {access.canEdit && (
            <TextField
              select
              label="Inspect a saved version"
              value={revision ?? ''}
              onChange={(event) => {
                setRevision(event.target.value ? Number(event.target.value) : null);
              }}
            >
              <MenuItem value="">Choose a version</MenuItem>
              {revisions.data?.revisions.map((item) => (
                <MenuItem key={item.version} value={item.version}>
                  Version {item.version} · {item.actorEmail} · {new Date(item.createdAt).toLocaleString()}
                </MenuItem>
              ))}
            </TextField>
          )}
          {access.canEdit && (revisionOffset > 0 || revisions.data?.nextOffset != null) && (
            <Stack direction="row">
              <Button
                disabled={revisionOffset === 0}
                onClick={() => setRevisionOffset(Math.max(0, revisionOffset - 100))}
              >
                Newer versions
              </Button>
              <Button
                disabled={revisions.data?.nextOffset == null}
                onClick={() => setRevisionOffset(revisions.data!.nextOffset!)}
              >
                Older versions
              </Button>
            </Stack>
          )}
          {candidate && (
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Setting</TableCell>
                    <TableCell>Current</TableCell>
                    <TableCell>{`Version ${revision}`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((change) => (
                    <TableRow key={change.field}>
                      <TableCell title={change.field}>{lensChangeLabel(change.field)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.before}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.after}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {changes.length === 0 && <Typography>No definition changes.</Typography>}
            </Box>
          )}
          {revision !== null && snapshot.data && access.canEdit && (
            <Button
              variant="contained"
              disabled={mutation.isPending || revision === lens.version}
              onClick={() => void run(`revisions/${revision}/restore`, { baseVersion: lens.version })}
              sx={{ alignSelf: 'flex-end' }}
            >
              Restore version {revision}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
